import QChart from '@/Components/chart.vue';
import Authenticated from '@/Layouts/Authenticated.vue';

import { debounce } from 'quasar';
import { 
    format, 
    formatDistance, 
    startOfWeek, 
    endOfWeek,
    parseISO
} from 'date-fns';

import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

// Methods
const Text = {
    toUSD(value) {
        if (value === undefined || value === null || value < 0) return null;

        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(value);
    },
    firstToUpper(value) {
        if (!value) return value;

        return value.charAt(0).toUpperCase() + value.slice(1);
    },
};

const Parse = {
    fixedFloat(value, precision = 2) {
        if (value['toFixed'] === undefined) return value;

        return parseFloat(value.toFixed(precision));
    },
};

const Dates = {
    getTimezone() {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
};

const Format = {
    formatDistance(date) { // date.mixin.js
        try {
            return formatDistance(new Date(date), new Date(), {
                addSuffix: true,
            });
        } catch (e) {
            return date;
        }
    },
    format(date, dateOnly = false) { // date.mixin.js
        try {
            if (dateOnly) {
                return format(new Date(date), 'MMM do uuuu');
            }
            return format(new Date(date), 'MMM do uuuu h:mm a');
        } catch (e) {
            return date;
        }
    },
    formatTo(_frmt, date = new Date()) { // date.mixin.js
        try {
            if (typeof date === 'string') {
                // NEEDED! This fixes time issues with UTC conversion
                return format(parseISO(date), _frmt);
            }

            return format(date, _frmt);
        } catch (e) {
            return date;
        }
    },
    formatByteCount(byteCount) {
        if (byteCount < 1024) {
            return `${byteCount} B`;
        }
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(byteCount) / Math.log(k));
        return `${(byteCount / Math.pow(k, i)).toPrecision(3)} ${sizes[i]}`;
    },
};

const Convert = {
    _pr_status(status) { // lms.mixin.js
        return {
            active: ['Active', 'positive'],
            inactive: ['Inactive', 'warning'],
            terminated: ['Terminated', 'negative'],
        }[status];
    },
    _status(status, color = false) { // lms.mixin.js
        if (!status) return 'Unknown';
        if (color) return this._statusColor(status);

        return {
            draft: 'Draft',
            pending: 'Pending',
            in_progress: 'In Progress',
            completed: 'Completed',
            paid: 'Paid',
            invoiced: 'Invoiced',
            processed: 'Processed',
        }[status];
    },
    _statusColor(status) { // lms.mixin.js
        if (!status) return 'primary';

        return (
            {
                draft: 'primary',
                pending: 'primary',
                in_progress: 'orange',
                completed: 'positive',
                paid: 'teal-7',
                invoiced: 'deep-orange-7',
                processed: 'black',
            }[status] ?? 'primary'
        );
    },
    editableStatus(status) { // lms.mixin.js
        return !['paid', 'processed', 'invoiced'].includes(status);
    },
    roleColor(role) { // lms.mixin.js
        switch ((role ?? '').toLowerCase()) {
            case 'administrator':
                return 'negative';
            case 'manager':
                return 'orange';
            case 'lumper':
                return 'blue-9';
            case 'lead':
                return 'green';
            default:
                return 'grey-10';
        }
    },
    hashCode(str) {
        // https://stackoverflow.com/a/8831937/10887412
        let hash = 0;
        for (let i = 0, len = str.length; i < len; i++) {
            let chr = str.charCodeAt(i);
            hash = (hash << 5) - hash + chr;
            hash |= 0; // Convert to 32bit integer
        }
        return hash;
    },
};

import {
    getCssVar, 
    colors,
} from 'quasar';

const { getPaletteColor, hexToRgb } = colors;

const Color = { 
    stringToHexColor(str) {
        return '#' + this.intToRgb(Convert.hashCode(str));
    },
    getContrastColor(hexColor) {
        if (!hexColor || hexColor.length < 7) return '#000000'

        // Convert hex to RGB
        const r = parseInt(hexColor.substr(1, 2), 16)
        const g = parseInt(hexColor.substr(3, 2), 16)
        const b = parseInt(hexColor.substr(5, 2), 16)

        // Calculate relative luminance (WCAG formula)
        const sRGB = [r, g, b].map(val => {
            val = val / 255
            return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4)
        })

        const luminance = 0.2126 * sRGB[0] + 0.7152 * sRGB[1] + 0.0722 * sRGB[2]

        // Return black for bright backgrounds, white for dark backgrounds
        return luminance > 0.179 ? '#000000' : '#FFFFFF'
    },
    intToRgb(i) {
        var c = (i & 0x00FFFFFF)
            .toString(16)
            .toUpperCase();

        return "00000".substring(0, 6 - c.length) + c;
    },
    /**
     * Calculate the brightness of a color
     * @param {String} input Hex color code or quasar variable
     * @param {String} light Hex color code or quasar variable
     * @param {String} dark Hex color code or quasar variable
     * @param {Number} ratio Brightness ratio
     * @returns  light or dark hex color depending on the brightness of the input color
     */
    calculateDarkOrLight(input, light, dark, ratio = 147) { // color.mixin.js
        if (!input.match(/#?([a-fA-F0-9]){6}/i)) {
            input = getCssVar(input) ?? getPaletteColor(input) ?? input;
        }

        if (!light.match(/#?([a-fA-F0-9]){6}/i)) {
            light = getCssVar(light) ?? getPaletteColor(light) ?? light;
        }

        if (!dark.match(/#?([a-fA-F0-9]){6}/i)) {
            dark = getCssVar(dark) ?? getPaletteColor(dark) ?? dark;
        }
        
        return this.isDarkRadial(input, light, dark, ratio);
    },
    /**
     * Calculate the brightness of a color
     * @param {String} input Hex color code or quasar variable
     * @param {String} light Hex color code or quasar variable
     * @param {String} dark Hex color code or quasar variable
     * @param {Number} ratio Brightness ratio
     * @returns light or dark color depending on the brightness of the input color
     */
    isDarkRadial(input, light, dark, ratio = 147) { // color.mixin.js
        if (typeof input !== 'string') {
            return light;
        }

        if (!input.match(/#?([a-fA-F0-9]){6}/i)) {
            return light;
        }
        // Get hex values to decimal
        const rgb = hexToRgb(input);
        
        const brightness = Math.round(
            (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000,
        );

        return brightness > ratio ? dark : light;
    },
    invertColor(hex) {
        function padZero(str, len) {
            len = len || 2;
            var zeros = new Array(len).join('0');
            return (zeros + str).slice(-len);
        }
        
        if (hex.indexOf('#') === 0) {
            hex = hex.slice(1);
        }
        // convert 3-digit hex to 6-digits.
        if (hex.length === 3) {
            hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }
        if (hex.length !== 6) {
            throw new Error('Invalid HEX color.');
        }
        // invert color components
        var r = (255 - parseInt(hex.slice(0, 2), 16)).toString(16),
            g = (255 - parseInt(hex.slice(2, 4), 16)).toString(16),
            b = (255 - parseInt(hex.slice(4, 6), 16)).toString(16);
        // pad each with zeros and return
        return '#' + padZero(r) + padZero(g) + padZero(b);
    },
    hexToRgb(hex) { // color.mixin.js
        // https://stackoverflow.com/a/5624139/10887412
        var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return {
            r: result ? parseInt(result[1], 16) : 0,
            g: result ? parseInt(result[2], 16) : 0,
            b: result ? parseInt(result[3], 16) : 0,
        };
    },
    rgbToHex(color) { // color.mixin.js
        if (typeof color === 'string') {
            return color;
        }

        const { r, g, b } = color;
        // https://stackoverflow.com/a/5624139/10887412
        return (
            '#' +
            ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
        ).split('.')[0];
    },
    colorDifference(
        r1,
        g1,
        b1,
        r2,
        g2,
        b2,
    ) { // color.mixin.js
        // https://www.reddit.com/r/learnprogramming/comments/18vjlm/javascript_find_closest_color_in_an_array_of/c8iff8x
        var sumOfSquares = 0;

        sumOfSquares += Math.pow(r1 - r2, 2);
        sumOfSquares += Math.pow(g1 - g2, 2);
        sumOfSquares += Math.pow(b1 - b2, 2);

        return sumOfSquares;
    },
    calculateGradientColor(value, min, max) { // color.mixin.js
        const normalizedValue = (value - min) / (max - min);
        let red = 255 - Math.round(normalizedValue * 255);
        let green = 255 - Math.round((1 - normalizedValue) * 255);
        const blue = 0;

        return this.rgbToHex({ r: red, g: green, b: blue });
    },
};

const Calculate = {
    getLongestStringInObj(_obj, ...exclude) {
        let longest = 0;
        for (let key in _obj) {
            if (exclude.includes(key)) {
                continue;
            }

            if ((_obj[key]?.length ?? -1) > longest) {
                longest = _obj[key].length;
            }
        }
        return longest;
    },
};

const Authorize = {
    __authUserExists() {
        return (this.$page.props.auth?.user ?? null) !== null;
    },
    GetRole() {
        if (!this.__authUserExists()) return false;
        return this.roles[this.$page.props.auth.user.role_id - 1];
    },
    isAdmin() {
        if (!this.__authUserExists()) return false;
        return this.$page.props.auth.user.role_id === 1;
    },
    isManager() {
        if (!this.__authUserExists()) return false;
        return this.$page.props.auth.user.role_id === 2;
    },
    isLead() {
        if (!this.__authUserExists()) return false;
        return this.$page.props.auth.user.role_id === 3;
    },
    isLumper() {
        if (!this.__authUserExists()) return false;
        return this.$page.props.auth.user.role_id === 4;
    },
    getUser() {
        if (!this.__authUserExists()) return false;
        return this.$page.props.auth.user;
    }
};

export default {
    components: {
        QChart,
        Authenticated,
    },
    data() {
        return {
            roles: ['Administrator', 'Manager', 'Lead', 'Lumper'],
            modifierColors: {
                other: 'accent',
                container_number: 'accent',
                sku_count: 'primary',
                hours: 'positive',
                piece_count: 'deep-orange',
                weight: 'teal',
                time_delay: 'deep-purple',
                pallet_count: 'pink',
            },
        };
    },
    methods: {
        route,
        ...Text,
        ...Parse,
        ...Color,
        ...Dates,
        ...Format,
        ...Convert,
        ...Calculate,
        ...Authorize,
        windowOpen(url) {
            window.open(url, '_blank');
        },
        visit(routeName, data = {}) {
            this.$inertia.visit(this.route(routeName), data);
        },
        tablePaginationLabel(pagination) { // pagination.mixin.js
            return this.paginationLabel({
                rows: pagination.rowsPerPage,
                total: pagination.rowsNumber,
                page: pagination.page,
            });
        },
        paginationLabel(pagination) { // pagination.mixin.js
            let { rows, total, page } = pagination;
            // Ensure everything is an integer
            rows = parseInt(rows);
            total = parseInt(total);
            page = parseInt(page);

            let from = (page - 1) * rows;

            const to = from + rows > total || !rows ? total : from + rows;

            from = !!from ? from : 1;

            const isFirstPage = page === 1;
            const isLastPage = page === Math.ceil(total / rows);

            return {
                to,
                from,
                pagination,

                isFirstPage,
                isLastPage,
                pagesNumber: Math.ceil(total / rows),
            };
        },
        calendarWeek() { // date.mixin.js
            const curr = new Date();
            const firstday = startOfWeek(curr, { weekStartsOn: 0 });
            const lastday = endOfWeek(curr, { weekStartsOn: 0 });

            return {
                today: format(curr, 'yyyy-LL-dd HH:mm:ss'),
                sunday: format(firstday, 'yyyy-LL-dd'),
                saturday: format(lastday, 'yyyy-LL-dd'),
            };
        },
        addEventListener(event, fn) {
            for (
                var i = 0, events = event.split(/[\s|,\s?]/g);
                i < events.length;
                i++
            ) {
                if (events[i].length > 0) {
                    document.addEventListener(events[i], fn);
                }
            }
        },
        truncateString(str, maxLen = 15, endingLen = 4, separator = '...') {
            if (str == undefined) return;
            if (str.length <= maxLen) return str;
            // Check if substring is a function
            if (typeof str.substring !== 'function') return str;

            return (
                str.substring(0, (maxLen - separator.length) - endingLen) +
                separator +
                str.substring(str.length - endingLen)
            );
        }
    },
    computed: {
        ChartJS: () => Chart,
        _debounce: () => debounce,
        $url: () => new URL(window.location.href),
        $format: () => format,
    },
};
